import axiosInstance from '../axiosInstance';

export interface EventSponsorsListData {
  items: EventSponsor[];
  total: number;
  page: number;
  limit: number;
}

export interface EventSponsorsResponse {
  status: boolean;
  message: string;
  data: EventSponsorsListData;
}

export interface EventSponsor {
  id: string;
  tenantId: string;
  name: string;
  description: string;
  order: number;
  baseTemplate: string;
  eventType: string;
  createdBy: string;
  updatedBy?: string | null;
}

export interface EventSponsorMutationResponse {
  status: boolean;
  message: string;
  data: EventSponsor;
}

export interface CreateEventSponsorParams {
  name: string;
  description: string;
  order: number;
  baseTemplate: string;
  eventType: string;
  tenantId: string;
  userId?: string;
}

export interface UpdateEventSponsorParams extends CreateEventSponsorParams {
  id: string;
}

export interface EventSponsorTiersByEventTypeResponse {
  status: boolean;
  message: string;
  data: EventSponsor[];
}

export interface EventSponsorshipItem {
  eventSponsorTierId: string;
  organizationId: string;
  displayOrder: number;
}

export interface CreateEventSponsorshipParams {
  tenantId: string;
  userId?: string;
  eventId: string;
  payload: EventSponsorshipItem[];
}

export interface CreateEventSponsorshipResponse {
  status: boolean;
  message: string;
  data?: unknown;
}

export interface EventSponsorshipRecord {
  id: string;
  eventId: string;
  eventSponsorTierId: string;
  organizationId: string;
  tenantId: string;
  baseTemplate?: string;
  displayOrder?: number;
}

export interface GetEventSponsorshipsByEventParams {
  tenantId: string;
  eventId: string;
}

export interface GetEventSponsorshipsByEventResponse {
  status: boolean;
  message: string;
  data: EventSponsorshipRecord[];
}

export interface MembersiteEventSponsorshipTier {
  eventSponsorTierId: string;
  name: string;
  displayOrder: number;
  organizationIds: string[];
  baseTemplate: string;
}

export interface GetMembersiteEventSponsorshipsParams {
  tenantId: string;
  eventId: string;
}

export interface GetMembersiteEventSponsorshipsResponse {
  status: boolean;
  message: string;
  data: MembersiteEventSponsorshipTier[];
}

export async function getEventSponsorTiers(params: {
  tenantId: string;
  page?: number;
  limit?: number;
  search?: string;
}): Promise<EventSponsorsResponse> {
  const { tenantId, page, limit, search } = params;
  const response = await axiosInstance.get(
    '/api/v1/sponsor/event-sponsor-tier',
    {
      headers: {
        'x-tenant-id': tenantId,
      },
      params: {
        page,
        limit,
        ...(search ? { search } : {}),
      },
    }
  );
  return response.data as EventSponsorsResponse;
}

export async function createEventSponsorTier(
  params: CreateEventSponsorParams
): Promise<EventSponsorMutationResponse> {
  const { tenantId, userId, ...payload } = params;
  const response = await axiosInstance.post(
    '/api/v1/sponsor/event-sponsor-tier',
    payload,
    {
      headers: {
        'x-tenant-id': tenantId,
        ...(userId ? { 'x-user-id': userId } : {}),
      },
    }
  );
  return response.data as EventSponsorMutationResponse;
}

export async function updateEventSponsorTier(
  params: UpdateEventSponsorParams
): Promise<EventSponsorMutationResponse> {
  const { tenantId, userId, id, ...payload } = params;
  const response = await axiosInstance.put(
    `/api/v1/sponsor/event-sponsor-tier/${id}`,
    payload,
    {
      headers: {
        'x-tenant-id': tenantId,
        ...(userId ? { 'x-user-id': userId } : {}),
      },
    }
  );
  return response.data as EventSponsorMutationResponse;
}

export async function getEventSponsorTiersByEventType(params: {
  tenantId: string;
  eventType: string;
}): Promise<EventSponsorTiersByEventTypeResponse> {
  const { tenantId, eventType } = params;

  const response = await axiosInstance.get(
    '/api/v1/sponsor/event-sponsor-tier/by-event-type',
    {
      headers: {
        'x-tenant-id': tenantId,
      },
      params: {
        eventType: eventType.trim(),
      },
    }
  );
  return response.data as EventSponsorTiersByEventTypeResponse;
}

export async function createSponsorships(
  params: CreateEventSponsorshipParams
): Promise<CreateEventSponsorshipResponse> {
  const { tenantId, userId, eventId, payload } = params;
  const response = await axiosInstance.post(
    '/api/v1/sponsor/event-sponsorship',
    { eventId, payload },
    {
      headers: {
        'x-tenant-id': tenantId,
        ...(userId ? { 'x-user-id': userId } : {}),
      },
    }
  );
  return response.data as CreateEventSponsorshipResponse;
}

export async function getEventSponsorshipsByEvent(
  params: GetEventSponsorshipsByEventParams
): Promise<GetEventSponsorshipsByEventResponse> {
  const { tenantId, eventId } = params;
  const response = await axiosInstance.get(
    `/api/v1/sponsor/event-sponsorship/by-event/${encodeURIComponent(eventId)}`,
    {
      headers: {
        'x-tenant-id': tenantId,
      },
    }
  );
  return response.data as GetEventSponsorshipsByEventResponse;
}

export async function getMembersiteEventSponsorships(
  params: GetMembersiteEventSponsorshipsParams
): Promise<GetMembersiteEventSponsorshipsResponse> {
  const { tenantId, eventId } = params;
  const response = await axiosInstance.get(
    `/api/v1/sponsor/event-sponsorship/membersite/${encodeURIComponent(
      eventId
    )}`,
    {
      headers: {
        'x-tenant-id': tenantId,
      },
    }
  );
  return response.data as GetMembersiteEventSponsorshipsResponse;
}
